import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Award, ChevronLeft, ChevronRight, Quote, X } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { useLiveTick } from '@/lib/liveContent';
import { CASE_STUDY_CATEGORIES, getCaseStudies } from '@/data/caseStudies';
import { trackEvent } from '@/lib/analytics';
import { SectionHeader } from '@/components/site/SectionHeader';
import { Reveal } from '@/components/site/Reveal';
import { CheckList } from '@/components/site/compass/shared';
import { CtaBand } from '@/components/site/CtaBand';
import { MarkdownBlock } from '@/lib/markdown';

const EASE = [0.22, 1, 0.36, 1];

// Public case study detail page. Content is CMS-driven (cms_case_studies,
// editable in the admin — including the project gallery); static fallback
// data covers items not yet migrated. Only published items render.
export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  useLiveTick();
  const reduce = useReducedMotion();

  const studies = getCaseStudies();
  const cs = studies.find((item) => item.slug === slug && item.status === 'published')
    || null;
  const gallery = (cs?.gallery || []).filter((g) => g?.src);
  const related = (cs?.related || [])
    .map((s) => studies.find((item) => item.slug === s))
    .filter((item) => item && item.status === 'published' && item.slug !== slug);

  const [lightbox, setLightbox] = useState(null); // index into gallery

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback(
    (dir) => setLightbox((i) => (i === null ? null : (i + dir + gallery.length) % gallery.length)),
    [gallery.length]
  );

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') stepLightbox(1);
      if (e.key === 'ArrowLeft') stepLightbox(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, closeLightbox, stepLightbox]);

  useSeo({
    title: cs?.seo?.title || (cs ? `${cs.title} | ${cs.client} Case Study | Amden Group` : 'Case Study | Amden Group'),
    description: cs?.seo?.description || cs?.summary || 'A verified Amden Group client engagement.',
    canonical: `https://amden.ie/case-studies/${slug}`,
    ogImage: cs?.image?.src,
    jsonLd: cs ? {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: 'Case Studies', item: 'https://amden.ie/case-studies' },
            { '@type': 'ListItem', position: 3, name: cs.title, item: `https://amden.ie/case-studies/${cs.slug}` },
          ],
        },
        {
          '@type': 'Article',
          headline: cs.title,
          description: cs.summary,
          about: { '@type': 'Organization', name: cs.client },
          ...(cs.image?.src ? { image: cs.image.src } : {}),
          author: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    } : undefined,
  });

  useEffect(() => {
    if (cs) trackEvent('case_study_view');
  }, [cs]);

  if (!cs) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="casestudy-notfound">
        <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Case study not found</h1>
        <p className="max-w-md text-sm leading-relaxed text-slate-600">
          This case study may have been moved or is no longer published.
        </p>
        <Link to="/case-studies" className="mt-2 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All case studies
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Hero */}
      <section aria-labelledby="casestudy-title" data-testid="casestudy-hero" className="relative overflow-hidden bg-navy-900 py-16 sm:py-20">
        <div className="grain-overlay" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs font-semibold text-slate-400">
            <Link to="/case-studies" className="inline-flex items-center gap-1.5 transition-colors hover:text-gold-500" data-testid="casestudy-back">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Case Studies
            </Link>
          </nav>
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold-500" data-testid="casestudy-client">{cs.client}</p>
              <h1 id="casestudy-title" className="mt-3 text-balance text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="casestudy-title">
                {cs.title}
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{cs.summary}</p>
              <ul className="mt-6 flex flex-wrap gap-2" aria-label="Engagement details" data-testid="casestudy-meta">
                {cs.industry && (
                  <li className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-200">{cs.industry}</li>
                )}
                {(cs.categories || []).map((cat) => (
                  <li key={cat} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-200">
                    {CASE_STUDY_CATEGORIES[cat]?.label || cat}
                  </li>
                ))}
              </ul>
            </Reveal>
            {cs.image?.src && (
              <Reveal delay={0.12}>
                <img
                  src={cs.image.src}
                  alt={cs.image.alt || `${cs.client} project`}
                  className="w-full rounded-xl border border-white/10 object-cover shadow-lift"
                  loading="eager"
                  data-testid="casestudy-hero-image"
                />
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Project overview — editorial paragraph + fact sheet */}
      {(cs.overview || cs.services?.length > 0) && (
        <section aria-labelledby="casestudy-overview-heading" data-testid="casestudy-overview" className="bg-white pt-16 sm:pt-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.6fr_1fr] lg:gap-14 lg:px-8">
            {cs.overview && (
              <Reveal>
                <h2 id="casestudy-overview-heading" className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Project overview</h2>
                <MarkdownBlock text={cs.overview} className="mt-3 text-base leading-relaxed text-slate-700" />
              </Reveal>
            )}
            <Reveal delay={0.08}>
              <dl className="rounded-xl border border-slate-200 bg-mist p-6" data-testid="casestudy-facts">
                <dt className="text-xs font-bold uppercase tracking-widest text-slate-500">Client</dt>
                <dd className="mt-0.5 text-sm font-bold text-navy-900">{cs.client}</dd>
                {cs.industry && (
                  <>
                    <dt className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Industry</dt>
                    <dd className="mt-0.5 text-sm font-bold text-navy-900">{cs.industry}</dd>
                  </>
                )}
                {cs.categories?.length > 0 && (
                  <>
                    <dt className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Service categories</dt>
                    <dd className="mt-0.5 text-sm font-bold text-navy-900">{cs.categories.map((c) => CASE_STUDY_CATEGORIES[c]?.label || c).join(', ')}</dd>
                  </>
                )}
                {cs.services?.length > 0 && (
                  <>
                    <dt className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">Services used</dt>
                    <dd className="mt-0.5 text-sm font-bold text-navy-900">{cs.services.join(', ')}</dd>
                  </>
                )}
              </dl>
            </Reveal>
          </div>
        </section>
      )}

      {/* Challenge + solution */}
      {(cs.challenge || cs.solution) && (
        <section aria-label="Challenge and solution" className="bg-white py-16 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8">
            {cs.challenge && (
              <Reveal>
                <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">The challenge</h2>
                <MarkdownBlock text={cs.challenge} className="mt-3 text-base leading-relaxed text-slate-700" testid="casestudy-challenge" />
              </Reveal>
            )}
            {cs.solution && (
              <Reveal delay={0.08}>
                <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">What Amden implemented</h2>
                <MarkdownBlock text={cs.solution} className="mt-3 text-base leading-relaxed text-slate-700" testid="casestudy-solution" />
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Verified outcomes + metrics */}
      {(cs.outcomes?.length > 0 || cs.metrics?.length > 0) && (
        <section aria-label="Outcomes" className="bg-mist py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Results"
              title="Verified outcomes"
              description={cs.metricsVerified ? 'Metrics verified with the client.' : undefined}
              testid="casestudy-outcomes-header"
            />
            {cs.metrics?.length > 0 && (
              <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="casestudy-metrics">
                {cs.metrics.map((m, i) => (
                  <Reveal key={m.label} delay={i * 0.06} className="h-full">
                    <li className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6">
                      <span className="text-3xl font-extrabold tracking-tight text-navy-900">{m.value}</span>
                      <span className="mt-1.5 text-sm font-semibold text-slate-600">{m.label}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            )}
            {cs.outcomes?.length > 0 && (
              <div className="mt-10 max-w-3xl">
                <CheckList items={cs.outcomes} testid="casestudy-outcomes" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Project gallery */}
      {gallery.length > 0 && (
        <section aria-labelledby="casestudy-gallery-heading" data-testid="casestudy-gallery" className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Project Gallery"
              title="The work in pictures"
              testid="casestudy-gallery-header"
            />
            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.map((img, i) => (
                <Reveal key={img.src + i} delay={(i % 3) * 0.06} className="h-full">
                  <li>
                    <button
                      type="button"
                      onClick={() => { setLightbox(i); trackEvent('case_study_gallery_open'); }}
                      data-testid={`casestudy-gallery-item-${i + 1}`}
                      aria-label={`Enlarge image ${i + 1}${img.caption ? `: ${img.caption}` : ''}`}
                      className="group block w-full overflow-hidden rounded-xl border border-slate-200 bg-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
                    >
                      <img
                        src={img.src}
                        alt={img.alt || ''}
                        loading="lazy"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      {img.caption && (
                        <span className="block px-4 py-3 text-left text-xs font-semibold text-slate-600">{img.caption}</span>
                      )}
                    </button>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {cs.testimonial?.quote && (
        <section aria-label="Client testimonial" className="bg-mist py-16 sm:py-20">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <Reveal>
              <Quote className="mx-auto h-8 w-8 text-gold-600" aria-hidden="true" />
              <blockquote className="mt-5 text-xl font-semibold leading-relaxed text-navy-900 sm:text-2xl" data-testid="casestudy-testimonial">
                “{cs.testimonial.quote}”
              </blockquote>
              <p className="mt-4 text-sm font-bold text-navy-900">{cs.testimonial.name}</p>
              <p className="text-xs font-semibold text-slate-500">
                {[cs.testimonial.position, cs.testimonial.company].filter(Boolean).join(' · ')}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Services used */}
      {cs.services?.length > 0 && (
        <section aria-label="Services used" className="bg-white py-14">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
              <Award className="h-4 w-4 text-navy-700" aria-hidden="true" /> Services used
            </h2>
            <ul className="mt-4 flex flex-wrap gap-2" data-testid="casestudy-services">
              {cs.services.map((s) => (
                <li key={s} className="rounded-full border border-slate-200 bg-mist px-3.5 py-1.5 text-xs font-bold text-navy-900">{s}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Related case studies */}
      {related.length > 0 && (
        <section aria-labelledby="casestudy-related-heading" data-testid="casestudy-related" className="bg-mist py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader align="left" eyebrow="Keep Reading" title="Related case studies" testid="casestudy-related-header" />
            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <li key={item.slug} className="h-full">
                  <Link
                    to={`/case-studies/${item.slug}`}
                    data-testid={`casestudy-related-${item.slug}`}
                    className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{item.client}</p>
                    <p className="mt-2 text-base font-extrabold tracking-tight text-navy-900">{item.title}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.summary}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy-900">
                      Read <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <CtaBand
        eyebrow="Your Project"
        heading="Facing a similar challenge?"
        copy="Tell us where your business needs to go — we will show you how we would approach it."
        primaryLabel="Book a Consultation"
        primaryHref="/contact#book-appointment"
        secondaryLabel="Explore Services"
        secondaryHref="/services"
      />

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && gallery[lightbox] && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={`Image ${lightbox + 1} of ${gallery.length}`}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center bg-navy-950/90 p-4"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={closeLightbox}
              aria-label="Close gallery"
              data-testid="lightbox-close"
              className="absolute right-4 top-4 rounded-full border border-white/20 bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
                  aria-label="Previous image"
                  data-testid="lightbox-prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
                  aria-label="Next image"
                  data-testid="lightbox-next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/10 p-2.5 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </>
            )}
            <figure className="max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <img
                src={gallery[lightbox].src}
                alt={gallery[lightbox].alt || ''}
                className="max-h-[78vh] w-auto max-w-full rounded-lg object-contain"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-3 text-center text-sm font-semibold text-slate-300">
                {gallery[lightbox].caption || gallery[lightbox].alt || ''}
                <span className="ml-2 text-slate-500">{lightbox + 1} / {gallery.length}</span>
              </figcaption>
            </figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
